import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { ShipmentMethodOption } from "@redotech/redo-customer-portal-shared/shipment-method/util";
import {
  DraftReturn,
  ShipmentMethod,
  ShipmentMethodName,
} from "@redotech/redo-model/draft-return/draft-return";
import {
  getCompensationMethod,
  hasReturnItemsThatRequireShipping,
} from "@redotech/redo-model/draft-return/util";
import { getResource } from "@redotech/redo-model/localization/resource";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import { Payer } from "@redotech/redo-model/return-flow";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Modal, ModalSize } from "@redotech/redo-web/modal";
import { Big } from "big.js";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { HappyReturns } from "./happy-returns";
import { PackagePickup } from "./package-pickup";
import { ReturnInStore } from "./return-in-store";
import { Settlement } from "./settlement";

export const ShipmentMethodsModal = memo(function ShipmentMethodsModal({
  draftReturn,
  setDraftReturn,
  onCancel,
  onComplete,
  settings,
}: {
  draftReturn: DraftReturn;
  setDraftReturn(draftReturn: DraftReturn): void;
  onCancel(): void;
  onComplete(method?: ShipmentMethod): void;
  settings: ReturnAppSettings;
}) {
  const client = useRequiredContext(RpcClientContext);
  const copyResource = getResource({
    variant: draftReturn.returnType,
    overrides: settings.resourceOverride,
  });

  const selectionRef = useRef<ShipmentMethod | null>(null);
  const [setMethodLoad, setMethod] = useTriggerLoad(async (signal) => {
    const selection = selectionRef.current!;
    try {
      const { draftReturn: updatedDraftReturn } =
        await client.setShipmentMethodInfo(
          { draftReturnId: draftReturn._id, shipmentMethod: selection },
          { signal },
        );
      setDraftReturn(updatedDraftReturn);
      onComplete?.(selection);
    } finally {
      selectionRef.current = null;
    }
  });

  const selectMethod = useHandler((selection: ShipmentMethod) => {
    selectionRef.current = selection;
    setMethod();
  });

  const updatePickupVariant = useHandler(async (pickupVariant: string) => {
    await client.updatePickupVariant({
      draftReturnId: draftReturn._id,
      pickupVariant,
    });
  });

  const shippingLoad = useLoad(
    async (signal) => {
      // If no shipping is required, then skip all shipping methods
      if (!hasReturnItemsThatRequireShipping(draftReturn)) {
        return [];
      }

      return await client.getShippingEstimate(
        { draftReturnId: draftReturn._id },
        { signal },
      );
    },
    [draftReturn],
  );

  const loading =
    shippingLoad.pending ||
    (shippingLoad.value === undefined && !shippingLoad.error) ||
    setMethodLoad.pending;
  const shipmentMethodOptions = useMemo(() => {
    if (!shippingLoad.value) {
      return [];
    }

    const load: ShipmentMethodOption[] = [];
    const shipments = shippingLoad.value;

    const totalShipmentFee = shipments.reduce((acc, shipment) => {
      return acc.add(shipment.fee);
    }, new Big(0));

    const labelPayer = getCompensationMethod(draftReturn)?.payer;

    if (
      shipments.length > 0 &&
      shipments.every((shipment) => !!shipment.happyReturnsDetails)
    ) {
      const totalHappyReturnsFee = shipments.reduce((acc, shipment) => {
        return acc.add(shipment.happyReturnsDetails!.price);
      }, new Big(0));

      load.push({
        type: ShipmentMethodName.HAPPY_RETURNS,
        nonPickupFee:
          labelPayer === Payer.CUSTOMER ? totalHappyReturnsFee : new Big(0),
        happyReturnsDetails: { ...shipments[0].happyReturnsDetails! },
      });

      load.push({
        type: ShipmentMethodName.SELF_SHIP,
        nonPickupFee:
          labelPayer === Payer.CUSTOMER ? totalShipmentFee : new Big(0),
      });
      return load;
    }

    const pickupFee =
      shipments?.length === 1 && shipments?.[0]?.pickupFee
        ? shipments[0].pickupFee
        : undefined;
    const pickupIneligibility =
      draftReturn.returnType === ReturnTypeEnum.WARRANTY
        ? "Warranty claims are disabled for pickup"
        : shipments?.length > 1
          ? "More than one package ineligible for pickup"
          : shipments.length === 1
            ? shipments[0].pickupIneligibility
            : undefined;

    if (pickupFee && !pickupIneligibility) {
      load.push({
        type: ShipmentMethodName.PICKUP,
        pickupFee: new Big(pickupFee),
        nonPickupFee:
          labelPayer === Payer.CUSTOMER ? totalShipmentFee : new Big(0),
      });
    }
    if (pickupIneligibility) {
      amplitude.logEvent("pickup-ineligible", { reason: pickupIneligibility });
    }

    // Check if return in store is available
    if (
      settings.returnInStoreEnabled &&
      draftReturn.returnType !== ReturnTypeEnum.WARRANTY
    ) {
      load.push({ type: ShipmentMethodName.IN_STORE });
    }

    // Check if settlement is available
    if (
      settings?.returns?.settlementEnabled &&
      settings?.returns?.settlementRefund &&
      settings?.returns?.settlementRefund > 0
    ) {
      const orderValue =
        getCompensationMethod(draftReturn)?.totalReturnableValue;
      if (orderValue?.gt(0)) {
        load.push({ type: ShipmentMethodName.SETTLEMENT, orderValue });
      }
    }

    return load;
  }, [
    draftReturn,
    settings.returnInStoreEnabled,
    settings?.returns?.settlementEnabled,
    settings?.returns?.settlementRefund,
    shippingLoad.value,
  ]);

  // If we are done loading and there are no shipping methods available, set to self-ship
  useEffect(() => {
    if (loading) {
      return;
    }
    if (shipmentMethodOptions?.length === 0 || !!shippingLoad.error) {
      selectMethod({ method: ShipmentMethodName.SELF_SHIP });
    }
  }, [loading, selectMethod, shipmentMethodOptions, shippingLoad]);

  // Start with the first option in the list, then iterate through them
  const [currentOptionIndex, setCurrentOptionIndex] = useState(0);
  const currentOption: ShipmentMethodOption | undefined =
    shipmentMethodOptions[currentOptionIndex];

  const nextOption = useHandler(() => {
    let nextIndex = currentOptionIndex + 1;

    // Don't show in-store modal if pickup is available
    if (
      shipmentMethodOptions?.[nextIndex]?.type ===
        ShipmentMethodName.IN_STORE &&
      shipmentMethodOptions.some(
        (option) => option?.type === ShipmentMethodName.PICKUP,
      )
    ) {
      nextIndex++;
    }

    if (nextIndex >= shipmentMethodOptions.length) {
      // If all the options have been skipped, set to self-ship
      selectMethod({ method: ShipmentMethodName.SELF_SHIP });
    } else {
      setCurrentOptionIndex(nextIndex);
    }
  });

  if (loading) {
    return <LoadingRedoAnimation />;
  }

  return (
    <Modal
      hideCloseButton
      onClose={onCancel}
      open
      showHeader={false}
      size={
        currentOption?.type === ShipmentMethodName.HAPPY_RETURNS
          ? ModalSize.FLEXIBLE
          : ModalSize.SMALL
      }
      title=""
    >
      {currentOption?.type === ShipmentMethodName.HAPPY_RETURNS ? (
        <HappyReturns
          onCancel={onCancel}
          options={shipmentMethodOptions}
          selectMethod={selectMethod}
        />
      ) : currentOption?.type === ShipmentMethodName.PICKUP ? (
        <PackagePickup
          copyResource={copyResource}
          draftReturn={draftReturn}
          nonPickupFee={currentOption.nonPickupFee?.toNumber()}
          onCancel={onCancel}
          onDecline={nextOption}
          pickupFee={currentOption.pickupFee?.toNumber()}
          selectMethod={selectMethod}
          showReturnInStoreOption={shipmentMethodOptions.some(
            (option) => option.type === ShipmentMethodName.IN_STORE,
          )}
          teamId={draftReturn.teamId}
          treatmentHash={draftReturn.treatmentHash}
          updatePickupVariant={updatePickupVariant}
          useBetaPackagePickupModal={settings.theme.useBetaPackagePickupModal}
          variants={settings.packagePickupVariants}
        />
      ) : currentOption?.type === ShipmentMethodName.IN_STORE ? (
        <ReturnInStore
          copyResource={copyResource}
          onAccept={() => selectMethod({ method: ShipmentMethodName.IN_STORE })}
          onCancel={onCancel}
          onDecline={nextOption}
        />
      ) : currentOption?.type === ShipmentMethodName.SETTLEMENT ? (
        <Settlement
          onAccept={() =>
            selectMethod({ method: ShipmentMethodName.SETTLEMENT })
          }
          onCancel={onCancel}
          onDecline={nextOption}
          orderValue={currentOption.orderValue ?? new Big("0")}
          settlementRefund={settings?.returns?.settlementRefund ?? 0}
        />
      ) : null}
    </Modal>
  );
});
